import { onRequest, RequestMethod } from "common/axios/axios"
import { getApiUrl } from "common/axios/getApiUrl"

export class MeetingRepository {
    // 모임 생성
    static onPostCreateMeeting = (params: any) => {
        return onRequest({
            url: getApiUrl('meetings'),
            method: RequestMethod.POST,
            params
        })
    }

    // 내 모임 목록 조회
    static onGetMyMeetings = (params?: { category?: string; status?: string; keyword?: string; ended?: boolean; page?: number; pageSize?: number }) => {
        return onRequest({
            url: getApiUrl('meetings') + '/list',
            method: RequestMethod.POST,
            params
        })
    }

    // 모임 상세 조회
    static onGetMeetingDetail = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}`,
            method: RequestMethod.GET
        })
    }

    // 모임 수정
    static onPutUpdateMeeting = (meetingId: string, params: any) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}`,
            method: RequestMethod.PUT,
            params
        })
    }

    // 모임 삭제
    static onDeleteMeeting = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}`,
            method: RequestMethod.DELETE
        })
    }

    // 정기모임 자동 롤오버 확인 — 주최자가 "다음 회차 OK" 눌러 pending 플래그 해제
    static onPostConfirmRollover = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/rollover/confirm`,
            method: RequestMethod.POST
        })
    }

    // 주최자 모임 즉시 종료 — meeting.ended_at = NOW()
    static onPostEndMeeting = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/end`,
            method: RequestMethod.POST
        })
    }

    // 주최자 정산 완료 — payment_settled_at = NOW(). 참여자 전체에 SETTLEMENT_COMPLETED 푸시
    static onPostSettleMeeting = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/settle`,
            method: RequestMethod.POST
        })
    }

    // 주최자 정산 완료 취소 — payment_settled_at = NULL + reminder 카운터 리셋
    static onDeleteSettleMeeting = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/settle`,
            method: RequestMethod.DELETE
        })
    }

    // ============================================================
    // 모임 후기 (Step 6+)
    // base path 별도 분리 (`/meeting-posts`) — `/meetings/{meetingId}` 패턴과 충돌 회피
    // ============================================================
    static onGetMeetingPosts = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/by-meeting/${meetingId}`,
            method: RequestMethod.GET
        })
    }

    static onPostMeetingPost = (meetingId: string, params: { content: string; images?: string[] }) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/by-meeting/${meetingId}`,
            method: RequestMethod.POST,
            params,
        })
    }

    static onDeleteMeetingPost = (postId: number) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/${postId}`,
            method: RequestMethod.DELETE
        })
    }

    static onGetPostComments = (postId: number) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/${postId}/comments`,
            method: RequestMethod.GET
        })
    }

    static onPostComment = (postId: number, content: string) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/${postId}/comments`,
            method: RequestMethod.POST,
            params: { content },
        })
    }

    static onDeleteComment = (commentId: number) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/comments/${commentId}`,
            method: RequestMethod.DELETE
        })
    }

    static onPostToggleLike = (postId: number) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/${postId}/like`,
            method: RequestMethod.POST
        })
    }

    static onPostReport = (params: {
        targetType: 'POST' | 'COMMENT';
        targetId: number;
        reasonCode: 'INAPPROPRIATE' | 'NSFW' | 'SPAM' | 'PRIVACY' | 'ETC';
        reasonText?: string;
    }) => {
        return onRequest({
            url: `${getApiUrl('meetingPosts')}/reports`,
            method: RequestMethod.POST,
            params,
        })
    }

    // 멤버 추방 (주최자 전용) — 추방 사유 (선택). 추방 멤버에게 감성 푸시 자동 발송
    static onDeleteMember = (meetingId: string, targetUserNo: number, reason?: string) => {
        const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/members/${targetUserNo}${qs}`,
            method: RequestMethod.DELETE
        })
    }

    // 도착 레이스 설정 변경 (주최자만)
    static onPutRaceSettings = (meetingId: string, params: {
        raceEnabled?: boolean;
        lateFeeAmount?: number;
        shuttlePenaltyEnabled?: boolean;
        lastPlacePenaltyType?: 'NONE' | 'SHUTTLE' | 'SHOT' | 'BOTH';
    }) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/race-settings`,
            method: RequestMethod.PUT,
            params
        })
    }

    // 레이스 순위 확정 (정각 이후 프론트 집계)
    static onPostRaceFinalize = (meetingId: string, rows: Array<{
        userNo: number;
        rankOrder: number;
        arrived: boolean;
        arrivedAt?: string | null;
        distanceMeters?: number | null;
        etaMinutes?: number | null;
        lateMinutes: number;
        isLast: boolean;
    }>) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/race/finalize`,
            method: RequestMethod.POST,
            params: { rows }
        })
    }

    // 레이스 결과 조회
    static onGetRaceResult = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/race/result`,
            method: RequestMethod.GET,
        })
    }

    // 라이브 순위 — 모임 시작 1시간 전 ~ 종료 사이 polling
    static onGetRaceLive = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/race/live`,
            method: RequestMethod.GET,
        })
    }

    // 지각 사유 보고 (본인)
    static onPostRaceExcuse = (meetingId: string, reason: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/race/excuse`,
            method: RequestMethod.POST,
            params: { reason }
        })
    }

    // 룰렛 스핀 — 실제 Payment 생성
    static onPostRouletteSpin = (params: {
        meetingId: string;
        storeName: string;
        totalAmount: number;
        participantUserNos: number[];
        maxPerPersonCap?: number;
    }) => {
        return onRequest({
            url: `${getApiUrl('paymentDetail')}/roulette/spin`,
            method: RequestMethod.POST,
            params,
        })
    }

    // 사유 승인 (주최자)
    static onPutRaceExcuseApprove = (meetingId: string, targetUserNo: number, approved: boolean) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/race/excuse/${targetUserNo}`,
            method: RequestMethod.PUT,
            params: { approved }
        })
    }

    // 모임 초대
    static onPostInviteToMeeting = (meetingId: string, params: any) => {
        return onRequest({
            url: `${getApiUrl('meetingInvite')}/${meetingId}/invite`,
            method: RequestMethod.POST,
            params
        })
    }

    // 대기 중인 초대자 조회 (PENDING + 미참여)
    static onGetPendingInvites = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingInvite')}/${meetingId}/pending-invites`,
            method: RequestMethod.GET,
        })
    }

    // 자주 함께한 사람 추천 — 예정 모임 친구 초대 카드용
    static onGetFrequentCoMembers = (meetingId: string, limit = 5) => {
        return onRequest({
            url: `${getApiUrl('meetingInvite')}/${meetingId}/frequent-co-members?limit=${limit}`,
            method: RequestMethod.GET,
        })
    }

    // 자주 함께한 사람 추천 (사용자 기준, 번개+모임 합산) — 번개 생성/공유 화면용 (meetingId 컨텍스트 없음)
    static onGetFrequentCoMembersByUser = (limit = 10) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/frequent-co-members/me?limit=${limit}`,
            method: RequestMethod.GET,
        })
    }

    // 자주 갔던 장소 추천 — 예정 모임 장소 카드용
    static onGetFrequentPlaces = (meetingId: string, limit = 5) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/frequent-places?limit=${limit}`,
            method: RequestMethod.GET,
        })
    }

    // 모임 장소 1클릭 변경 — placeId(추천) 또는 새 장소 정보(이름/주소/좌표)
    static onPutMeetingPlace = (
        meetingId: string,
        params: {
            placeId?: string;
            placeName?: string;
            placeAddress?: string;
            latitude?: number | string;
            longitude?: number | string;
        },
    ) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/place`,
            method: RequestMethod.PUT,
            params,
        })
    }

    // 모임 장소 해제 — place_id = NULL
    static onDeleteMeetingPlace = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/place`,
            method: RequestMethod.DELETE,
        })
    }

    // 내가 받은 PENDING 초대 meetingId 목록 — 홈 배너 교차 검증용
    static onGetMyPendingInvites = () => {
        return onRequest({
            url: `${getApiUrl('meetingInvite')}/my-pending-invites`,
            method: RequestMethod.GET,
        })
    }

    // 내가 받은 PENDING 초대 상세 — 홈 배너 직접 렌더용 (푸시 미수신 대응)
    static onGetMyPendingInviteDetails = () => {
        return onRequest({
            url: `${getApiUrl('meetingInvite')}/my-pending-invites/details`,
            method: RequestMethod.GET,
        })
    }

    // 재촉(nudge) 푸시 발송 — 선택한 userNo 들에게 MEETING_NUDGE 랜덤 변형 푸시
    static onPostNudgeInvitees = (meetingId: string, userNos: number[]) => {
        return onRequest({
            url: `${getApiUrl('meetingInvite')}/${meetingId}/nudge`,
            method: RequestMethod.POST,
            params: { userNos },
        })
    }

    // 모임 참여
    static onPostJoinMeeting = (meetingId: string, params?: any) => {
        return onRequest({
            url: `${getApiUrl('meetingJoin')}/${meetingId}/join`,
            method: RequestMethod.POST,
            params
        })
    }

    // 초대 코드로 모임 참여 (로그인 사용자)
    static onPostJoinMeetingByCode = (inviteCode: string) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/join-by-code?inviteCode=${inviteCode}`,
            method: RequestMethod.POST
        })
    }

    // 초대 코드로 모임 정보 조회 (공개 — 로그인 불필요)
    static onGetMeetingByInviteCode = (inviteCode: string) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/invite-info?inviteCode=${inviteCode}`,
            method: RequestMethod.GET,
            skipAuth: true,
        })
    }

    // 게스트로 모임 참여 (로그인 불필요 — 이름/전화번호만)
    static onPostJoinMeetingAsGuest = (inviteCode: string, params: { nickname: string; phone: string }) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/join-as-guest?inviteCode=${inviteCode}`,
            method: RequestMethod.POST,
            params,
            skipAuth: true,
        })
    }

    // 게스트 로그인 — 이름+전화번호로 기존 게스트 인증 (로그인 불필요)
    static onPostGuestLogin = (meetingId: string, params: { nickname: string; phone: string }) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/guest-login?meetingId=${meetingId}`,
            method: RequestMethod.POST,
            params,
            skipAuth: true,
        })
    }

    // 모임 탈퇴
    static onPostLeaveMeeting = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingLeave')}/${meetingId}/leave`,
            method: RequestMethod.POST
        })
    }

    // 모임 멤버 조회
    static onGetMeetingMembers = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingMembers')}/${meetingId}/members`,
            method: RequestMethod.GET
        })
    }

    // 참석 상태 변경 (legacy — 단일 필드만)
    static onPutUpdateAttendance = (meetingId: string, attendanceStatus: 'ATTENDING' | 'NOT_ATTENDING' | 'UNDECIDED') => {
        return onRequest({
            url: `${getApiUrl('meetingMembers')}/${meetingId}/members/attendance?attendanceStatus=${attendanceStatus}`,
            method: RequestMethod.PUT
        })
    }

    // 내 멤버 정보 일괄 변경 (참석 + 위치공유) — 권장 API
    static onPutMyMembership = (
        meetingId: string,
        params: { attendanceStatus?: 'ATTENDING' | 'NOT_ATTENDING' | 'UNDECIDED'; locationSharing?: boolean }
    ) => {
        return onRequest({
            url: `${getApiUrl('meetingMembers')}/${meetingId}/members/me`,
            method: RequestMethod.PUT,
            params
        })
    }

    // ========================================
    // 장소 후보 투표 (모임 시작 전)
    // ========================================

    /**
     * 장소 후보 목록 — 표 수, 내 투표 여부, 투표 가능 여부, 방장 여부까지 한 번에.
     *
     * 모으는 단계에서 방장이 후보를 올리고 참여자가 투표합니다. 모임이 시작하면
     * 투표는 닫히고 장소는 누구나 바로 바꿉니다(변경 이력이 남습니다).
     */
    static onGetPlaceCandidates = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/${meetingId}/place-candidates`,
            method: RequestMethod.GET,
        })
    }

    /** 후보 등록 — 방장만 */
    static onPostPlaceCandidate = (meetingId: string, params: {
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
        externalPlaceId?: string;
        imageUrl?: string;
        memo?: string;
    }) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/${meetingId}/place-candidates`,
            method: RequestMethod.POST,
            params,
        })
    }

    /** 후보 삭제 — 방장만 */
    static onDeletePlaceCandidate = (meetingId: string, candidateId: string) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/${meetingId}/place-candidates/${candidateId}`,
            method: RequestMethod.DELETE,
        })
    }

    /** 투표 / 취소 — 후보당 1표, 여러 후보 가능 */
    static onVotePlaceCandidate = (meetingId: string, candidateId: string, on: boolean) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/${meetingId}/place-candidates/${candidateId}/vote`,
            method: RequestMethod.POST,
            query: { on: String(on) },
        })
    }

    /** 확정 — 방장만. 후보와 표는 남습니다(어떻게 정해졌는지 보여야 하므로) */
    static onConfirmPlaceCandidate = (meetingId: string, candidateId: string) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/${meetingId}/place-candidates/${candidateId}/confirm`,
            method: RequestMethod.POST,
        })
    }

    // ========================================
    // 변경 이력
    // ========================================

    /**
     * 모임 변경 이력 — 확정 후 누가 무엇을 어떻게 바꿨는가. 최신순.
     *
     * 상세의 「마지막 변경」 한 줄을 누르면 이걸 불러 시트로 보여줍니다.
     * 멤버만 조회할 수 있습니다(서버에서 검증).
     */
    static onGetChangeLogs = (meetingId: string, page = 0, size = 20) => {
        return onRequest({
            url: `${getApiUrl('meetings')}/${meetingId}/change-logs`,
            method: RequestMethod.GET,
            query: { page, size },
        })
    }

    // ========================================
    // 서브 장소 관리
    // ========================================

    // 서브 장소 목록 조회
    static onGetSubPlaces = (meetingId: string, params?: { category?: string; dayNumber?: number }) => {
        let url = `${getApiUrl('subPlaces')}/${meetingId}/sub-places`;
        const queryParams = new URLSearchParams();
        if (params?.category) queryParams.append('category', params.category);
        if (params?.dayNumber !== undefined) queryParams.append('dayNumber', params.dayNumber.toString());
        if (queryParams.toString()) url += `?${queryParams.toString()}`;
        return onRequest({
            url,
            method: RequestMethod.GET
        })
    }

    // 서브 장소 단건 조회
    static onGetSubPlace = (meetingId: string, subPlaceId: number) => {
        return onRequest({
            url: `${getApiUrl('subPlaces')}/${meetingId}/sub-places/${subPlaceId}`,
            method: RequestMethod.GET
        })
    }

    // 서브 장소 생성
    static onPostCreateSubPlace = (meetingId: string, params: {
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
        category: string;
        sortOrder?: number;
        dayNumber?: number;
        visitTime?: string;
        startDatetime?: string;
        endDatetime?: string;
        description?: string;
        imageUrl?: string;
        rating?: number;
        priceRange?: string;
        tags?: string;
    }) => {
        return onRequest({
            url: `${getApiUrl('subPlaces')}/${meetingId}/sub-places`,
            method: RequestMethod.POST,
            params
        })
    }

    // 서브 장소 일괄 생성
    static onPostCreateSubPlaces = (meetingId: string, params: Array<{
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
        category: string;
        sortOrder?: number;
        dayNumber?: number;
        visitTime?: string;
        startDatetime?: string;
        endDatetime?: string;
        description?: string;
    }>) => {
        return onRequest({
            url: `${getApiUrl('subPlaces')}/${meetingId}/sub-places/batch`,
            method: RequestMethod.POST,
            params
        })
    }

    // 서브 장소 수정
    static onPutUpdateSubPlace = (meetingId: string, subPlaceId: number, params: {
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
        category: string;
        sortOrder?: number;
        dayNumber?: number;
        visitTime?: string;
        startDatetime?: string;
        endDatetime?: string;
        description?: string;
        imageUrl?: string;
        rating?: number;
        priceRange?: string;
        tags?: string;
    }) => {
        return onRequest({
            url: `${getApiUrl('subPlaces')}/${meetingId}/sub-places/${subPlaceId}`,
            method: RequestMethod.PUT,
            params
        })
    }

    // 서브 장소 삭제
    static onDeleteSubPlace = (meetingId: string, subPlaceId: number) => {
        return onRequest({
            url: `${getApiUrl('subPlaces')}/${meetingId}/sub-places/${subPlaceId}`,
            method: RequestMethod.DELETE
        })
    }
}
